import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { CartModel } from '@models/cart';
import { AddressModel } from '@models/address';

import { updateCart as updateCartAction } from '@actions/panel/cart_view/carts';

import CustomInput from '@components/shared/panel/CustomInput';
import InlineEditButtons from '@components/shared/panel/InlineEditButtons';

import http from '@/utils/http';

class AddressItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      address: props.address,
      addressCopy: props.address,
      activeEdit: false,
      processing: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (_.isEqual(prevProps.address, this.props.address)) return;
    this.setState({ address: this.props.address });
  }

  handleInputChange = ({ target: { name, value } }) => {
    this.setState((prevState) => {
      const address = new AddressModel({ ...prevState.address });

      if (name === 'state') {
        address.set('state', value.toUpperCase());
      } else {
        address.set(name, value);
      }

      return { address, activeEdit: true };
    });
  };

  handleCheckboxChange = ({ target: { name, checked } }) => {
    this.setState((prevState) => {
      const address = new AddressModel({ ...prevState.address });
      address.set(name, checked);

      return { address, activeEdit: true };
    });
  };

  updateAddress = () => {
    const { address } = this.state;

    this.setState({ processing: true });

    http.put(AddressModel.routes.addressPath(address.id), { shipping_address: address.attributes })
      .then((response) => {
        const newAddress = new AddressModel(response.data);
        this.setState({ address: newAddress, addressCopy: newAddress, activeEdit: false });

        window.flashMessages.addMessage({ type: 'success', text: 'Address has been updated.' });
      })
      .catch((error) => {
        const invalidAddress = new AddressModel(error.response.data);
        this.setState({ address: invalidAddress });

        window.flashMessages.addMessage({ type: 'error', text: 'Address cannot be updated due to some errors.' });
      })
      .finally(() => this.setState({ processing: false }));
  }

  handleCancelEdit = () => {
    this.setState((prevState) => ({ address: prevState.addressCopy, activeEdit: false }));
  }

  handleAddressChange = () => {
    this.assignAddressTemplate();
  }

  assignAddressTemplate = () => {
    const { updateCart, currentCart } = this.props;
    const { address } = this.state;

    http.put(currentCart.routes.byId, { cart: { shipping_address_id: address.id } })
      .then((response) => {
        updateCart(response.data);

        window.flashMessages.addMessage({
          type: 'success',
          text: 'Cart has been updated.',
        });
      })
      .catch(() => {
        window.flashMessages.addMessage({
          type: 'error',
          text: 'Cart cannot be updated due to some errors.',
        });
      });
  }

  handleStatusChange = (newStatus) => {
    const { address } = this.state;
    const { onAddressUpdate } = this.props;

    this.setState({ processing: true });

    const updatedAddress = new AddressModel({ ...address.attributes, status: newStatus });
    const statusMessages = {
      active: {
        success: 'Address has been activated.',
        error: 'Address cannot be activated due to some errors.'
      },
      inactive: {
        success: 'Address has been marked as inactive.',
        error: 'Address cannot be inactivated due to some errors.'
      }
    };

    http.put(AddressModel.routes.addressPath(address.id), { shipping_address: updatedAddress.attributes })
      .then((response) => {
        const newAddress = new AddressModel(response.data);
        this.setState({ address: newAddress, addressCopy: newAddress });

        // Notify parent component to update its address collection
        if (onAddressUpdate) {
          onAddressUpdate(newAddress);
        }

        window.flashMessages.addMessage({ 
          type: 'success', 
          text: statusMessages[newStatus].success 
        });
      })
      .catch((error) => {
        const invalidAddress = new AddressModel(error.response.data);
        this.setState({ address: invalidAddress });

        window.flashMessages.addMessage({ 
          type: 'error', 
          text: statusMessages[newStatus].error 
        });
      })
      .finally(() => this.setState({ processing: false }));
  }

  handleActivateAddress = () => {
    this.handleStatusChange('active');
  }

  handleInactivateAddress = () => {
    this.handleStatusChange('inactive');
  }

  render() {
    const {
      address: {
        id,
        street_1,
        street_2,
        city,
        state,
        zip,
        type,
        verified,
        company,
        first_name,
        last_name,
        is_valid,
        errors,
        editable_for_admin,
        can_show_use_button,
        used_by_restricted_carts,
        status,
      },
      activeEdit,
      processing,
    } = this.state;

    const { currentCart } = this.props;

    const isAddressSelected = id === currentCart.shipping_address_id;
    const isEditable = editable_for_admin !== false;
    const showUseButton = can_show_use_button === true;
    const isInactive = status === 'inactive' || status === 1;

    const isRestrictedCart = currentCart && 
      ['shipping', 'completed'].includes(currentCart.stage);

    const shouldShowButton = !isRestrictedCart || showUseButton;
    const shouldShowUseButton = shouldShowButton && !isInactive;

    return (
      <tr className={`align-center ${isAddressSelected ? 'table-success' : ''}`}>
        <td>
          {activeEdit && isEditable && (
            <InlineEditButtons
              approveAction={this.updateAddress}
              cancelAction={this.handleCancelEdit}
              processing={processing}
            />
          )}

          {!activeEdit && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {!isAddressSelected && currentCart.isPresent && shouldShowUseButton ? (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={this.handleAddressChange}
                  disabled={!is_valid}
                  style={{ marginRight: '0.5rem' }}
                >
                  Use
                </button>
              ) : (
                <div style={{ width: '43px', marginRight: '0.5rem' }}></div>
              )}
              {isInactive && (
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={this.handleActivateAddress}
                  disabled={processing}
                >
                  Active
                </button>
              )}
              {!isInactive && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={this.handleInactivateAddress}
                  disabled={processing}
                >
                  Inactive
                </button>
              )}
            </div>
          )}

          {!isEditable && !used_by_restricted_carts && (
            <span className="text-muted ml-2 small">
              Not editable
            </span>
          )}
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.street_1 && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="street_1"
            onChange={this.handleInputChange}
            value={street_1 || ''}
            error={errors.street_1 ? errors.street_1[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.street_2 && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="street_2"
            onChange={this.handleInputChange}
            value={street_2 || ''}
            error={errors.street_2 ? errors.street_2[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.city && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="city"
            onChange={this.handleInputChange}
            value={city || ''}
            error={errors.city ? errors.city[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.state && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="state"
            maxLength="2"
            onChange={this.handleInputChange}
            value={state || ''}
            error={errors.state ? errors.state[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.zip && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="zip"
            onChange={this.handleInputChange}
            value={zip || ''}
            error={errors.zip ? errors.zip[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.type && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="type"
            onChange={this.handleInputChange}
            value={type || ''}
            error={errors.type ? errors.type[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <input
            type="checkbox"
            name="verified"
            id={`${id}-verified`}
            onChange={this.handleCheckboxChange}
            checked={verified}
            disabled={!isEditable}
          />
          <label htmlFor={`${id}-verified`} />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.company && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="company"
            onChange={this.handleInputChange}
            value={company || ''}
            error={errors.company ? errors.company[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.first_name && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="first_name"
            onChange={this.handleInputChange}
            value={first_name || ''}
            error={errors.first_name ? errors.first_name[0] : null}
            disabled={!isEditable}
          />
        </td>
        <td>
          <CustomInput
            type="text"
            className={`form-control form-control-sm ${errors.last_name && 'is-invalid'} ${!isEditable ? 'bg-light' : ''}`}
            name="last_name"
            onChange={this.handleInputChange}
            value={last_name || ''}
            error={errors.last_name ? errors.last_name[0] : null}
            disabled={!isEditable}
          />
        </td>
      </tr>
    );
  }
}

const mapStateToProps = (state) => ({
  currentCart: new CartModel(state.carts.current_cart),
});

const mapDispatchToProps = (dispatch) => ({
  updateCart: (cart) => dispatch(updateCartAction(cart)),
});

AddressItem.propTypes = {
  currentCart: PropTypes.instanceOf(CartModel).isRequired,
  address: PropTypes.instanceOf(AddressModel).isRequired,
  onAddressUpdate: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddressItem);
